import React from "react";
import {
  AbsoluteFill,
  Audio,
  Img,
  interpolate,
  spring,
  staticFile,
  useCurrentFrame,
  useVideoConfig,
  Easing,
} from "remotion";
import {
  CheckCircle2,
  XCircle,
  Zap,
  ArrowRight,
  QrCode,
  BookOpen,
  GraduationCap,
  Briefcase,
  Mail,
  MessageSquare,
  Globe,
  Clock,
  Sparkles,
  Layers,
  FileText,
  AlertTriangle,
} from "lucide-react";

export const NixlevKineticFinal: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  // Premium Brand Palette
  const bgSand = "#F6F4EE";
  const greenForest = "#1B3B2B";
  const greenEmerald = "#10B981";
  const goldAccent = "#D4A346";
  const redAlert = "#E63946";
  const textDark = "#121814";
  const textMuted = "#6B7280";

  // Harmonic subtle bobs
  const bob1 = Math.sin(frame / 18) * 8;
  const bob2 = Math.cos(frame / 22) * 6;

  // Exact Subtitles mapping with audio timings (Safe Zone: bottom 220px)
  const SUBTITLES = [
    {
      from: 0,
      to: 105,
      text: "Jualan produk digital di bio link sering bikin nyesek.",
      highlight: "sering bikin nyesek",
    },
    {
      from: 105,
      to: 210,
      text: "Calon pembeli udah tertarik, tapi pas klik link...",
      highlight: "pas klik link",
    },
    {
      from: 210,
      to: 355,
      text: "Disuruh chat WA manual, nunggu admin & kirim bukti.",
      highlight: "chat WA manual",
    },
    {
      from: 355,
      to: 480,
      text: "Ujung-ujungnya? Ghosting. Batal beli.",
      highlight: "Ghosting. Batal beli.",
      isRed: true,
    },
    {
      from: 480,
      to: 630,
      text: "Sekarang, bedah cara mainnya bareng Nixlev.",
      highlight: "Nixlev",
    },
    {
      from: 630,
      to: 810,
      text: "Dalam 5 menit, bikin halaman jualan profesional + checkout otomatis.",
      highlight: "checkout otomatis",
    },
    {
      from: 810,
      to: 980,
      text: "Tinggal taruh e-book, template Notion, kelas video, atau jasamu.",
      highlight: "e-book, template, kelas, jasa",
    },
    {
      from: 980,
      to: 1110,
      text: "Pembeli tinggal klik & scan QRIS langsung di layar...",
      highlight: "scan QRIS",
    },
    {
      from: 1110,
      to: 1310,
      text: "Boom! File otomatis terkirim ke WhatsApp & Email 24 jam nonstop!",
      highlight: "WhatsApp & Email 24 jam nonstop!",
    },
    {
      from: 1310,
      to: 1460,
      text: "Gak ada lagi drama begadang cek mutasi atau sewa hosting jutaan.",
      highlight: "Gak ada lagi drama",
    },
    {
      from: 1460,
      to: 1560,
      text: "Saatnya bio link kamu bekerja menghasilkan uang otomatis.",
      highlight: "menghasilkan uang otomatis",
    },
    {
      from: 1560,
      to: 1700,
      text: "Bikin gratis sekarang di nixlev.com 🚀",
      highlight: "nixlev.com",
    },
  ];

  const currentSub = SUBTITLES.find(
    (s) => frame >= s.from && frame < s.to
  );

  return (
    <AbsoluteFill
      style={{
        backgroundColor: frame >= 1460 ? greenForest : bgSand,
        fontFamily:
          "-apple-system, BlinkMacSystemFont, 'SF Pro Display', 'Inter', sans-serif",
        color: frame >= 1460 ? "#FFFFFF" : textDark,
        overflow: "hidden",
        transition: "background-color 0.4s ease",
      }}
    >
      {/* 1. Dynamic Ambient Background Mesh */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          background:
            frame >= 1460
              ? `radial-gradient(circle at 50% 30%, rgba(16, 185, 129, 0.15) 0%, transparent 70%)`
              : `radial-gradient(circle at ${50 + Math.sin(frame / 50) * 18}% ${
                  35 + Math.cos(frame / 45) * 15
                }%, rgba(27, 59, 43, 0.08) 0%, transparent 65%)`,
          pointerEvents: "none",
        }}
      />

      {/* 2. Full Creative Audio Track (Gemini TTS Orus - 55.13s) */}
      <Audio src={staticFile("nixlev_creative_orus.mp3")} />

      {/* 3. Micro Sound Design & Rhythmic SFX */}
      {frame === 0 && <Audio src={staticFile("whoosh-short.mp3")} volume={0.4} />}
      {frame === 75 && <Audio src={staticFile("click.mp3")} volume={0.7} />}
      {frame === 150 && <Audio src={staticFile("click.mp3")} volume={0.9} />}

      {frame === 260 && <Audio src={staticFile("whoosh.mp3")} volume={0.5} />}
      {frame === 320 && <Audio src={staticFile("chime.mp3")} volume={0.6} />}

      {frame === 480 && <Audio src={staticFile("whoosh.mp3")} volume={0.45} />}
      {frame === 540 && <Audio src={staticFile("click-soft.mp3")} volume={0.5} />}
      {frame === 580 && <Audio src={staticFile("click-soft.mp3")} volume={0.5} />}
      {frame === 620 && <Audio src={staticFile("click-soft.mp3")} volume={0.5} />}
      {frame === 660 && <Audio src={staticFile("click-soft.mp3")} volume={0.5} />}

      {frame === 780 && <Audio src={staticFile("whoosh-short.mp3")} volume={0.5} />}
      {frame === 860 && <Audio src={staticFile("click.mp3")} volume={0.8} />}
      {frame === 940 && <Audio src={staticFile("chime.mp3")} volume={0.9} />}
      {frame === 980 && <Audio src={staticFile("click-soft.mp3")} volume={0.7} />}
      {frame === 1040 && <Audio src={staticFile("click-soft.mp3")} volume={0.7} />}

      {frame === 1200 && <Audio src={staticFile("whoosh.mp3")} volume={0.45} />}
      {frame === 1300 && <Audio src={staticFile("click.mp3")} volume={0.7} />}

      {frame === 1460 && <Audio src={staticFile("whoosh.mp3")} volume={0.55} />}
      {frame === 1520 && <Audio src={staticFile("chime.mp3")} volume={0.85} />}

      {/* =========================================================================
          BEAT 1: THE FRUSTRATION & GHOSTING DROP-OFF (Frames 0 - 260 / 0.0s - 8.7s)
          ========================================================================= */}
      {frame < 260 && (
        <AbsoluteFill
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "flex-start",
            padding: "80px 40px",
          }}
        >
          {/* Top Friction Pill */}
          <div
            style={{
              padding: "12px 28px",
              backgroundColor: "#FEE2E2",
              color: redAlert,
              borderRadius: 30,
              fontSize: 24,
              fontWeight: 800,
              letterSpacing: "0.06em",
              marginBottom: 25,
              textTransform: "uppercase",
              display: "flex",
              alignItems: "center",
              gap: 12,
              transform: `scale(${spring({
                frame,
                fps,
                config: { damping: 14, stiffness: 120 },
              })})`,
            }}
          >
            <AlertTriangle size={26} />
            Dilema Jualan Bio Link Biasa
          </div>

          <h1
            style={{
              fontSize: 54,
              fontWeight: 900,
              textAlign: "center",
              lineHeight: 1.2,
              marginBottom: 35,
              letterSpacing: "-0.03em",
              maxWidth: 900,
            }}
          >
            Pernah ngerasa <span style={{ color: redAlert }}>calon pembeli kabur</span> saat mau beli?
          </h1>

          {/* 3D Isometric Phone with Realistic WhatsApp Chat Simulation */}
          <div
            style={{
              width: 580,
              height: 980,
              backgroundColor: "#111827",
              borderRadius: 54,
              padding: 16,
              boxShadow: "0 40px 90px rgba(0,0,0,0.22)",
              transform: `perspective(1200px) rotateY(${interpolate(
                frame,
                [0, 260],
                [-8, 6],
                { easing: Easing.inOut(Easing.quad) }
              )}deg) rotateX(6deg) translateY(${bob1}px)`,
              display: "flex",
              flexDirection: "column",
            }}
          >
            {/* Phone Screen Container */}
            <div
              style={{
                flex: 1,
                backgroundColor: "#EFEAE2",
                borderRadius: 42,
                overflow: "hidden",
                display: "flex",
                flexDirection: "column",
                position: "relative",
              }}
            >
              {/* WhatsApp Header */}
              <div
                style={{
                  height: 90,
                  backgroundColor: "#075E54",
                  color: "#FFFFFF",
                  display: "flex",
                  alignItems: "center",
                  padding: "0 24px",
                  gap: 16,
                }}
              >
                <div
                  style={{
                    width: 52,
                    height: 52,
                    borderRadius: "50%",
                    backgroundColor: "#128C7E",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontWeight: 800,
                    fontSize: 22,
                  }}
                >
                  K
                </div>
                <div>
                  <div style={{ fontSize: 24, fontWeight: 700 }}>Admin Kreator</div>
                  <div style={{ fontSize: 16, color: "#A7F3D0" }}>Terakhir dilihat 4 jam lalu</div>
                </div>
              </div>

              {/* Chat Content Body */}
              <div style={{ flex: 1, padding: 24, display: "flex", flexDirection: "column", gap: 18 }}>
                {/* User Chat Bubble */}
                <div
                  style={{
                    alignSelf: "flex-end",
                    backgroundColor: "#DCF8C6",
                    color: "#1F2937",
                    padding: "16px 20px",
                    borderRadius: "18px 18px 4px 18px",
                    maxWidth: "80%",
                    fontSize: 20,
                    fontWeight: 600,
                    boxShadow: "0 2px 6px rgba(0,0,0,0.08)",
                  }}
                >
                  "Halo kak, mau order E-Book Notion & Canva nya dong, minta no rek BCA ya"
                  <div style={{ fontSize: 14, color: "#6B7280", textAlign: "right", marginTop: 4 }}>
                    14:15 ✓✓
                  </div>
                </div>

                {/* Waiting State */}
                {frame >= 60 && (
                  <div
                    style={{
                      alignSelf: "center",
                      backgroundColor: "rgba(255,255,255,0.85)",
                      padding: "10px 22px",
                      borderRadius: 20,
                      fontSize: 16,
                      fontWeight: 700,
                      color: "#6B7280",
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                    }}
                  >
                    <Clock size={18} color="#EF4444" />
                    Menunggu balasan admin... (3 jam)
                  </div>
                )}

                {/* Stalled Payment Proof Demand */}
                {frame >= 100 && (
                  <div
                    style={{
                      alignSelf: "flex-start",
                      backgroundColor: "#FFFFFF",
                      color: "#1F2937",
                      padding: "16px 20px",
                      borderRadius: "18px 18px 18px 4px",
                      maxWidth: "85%",
                      fontSize: 20,
                      fontWeight: 600,
                    }}
                  >
                    "BCA 8821xxxxxx a/n Admin. Kalau sudah transfer tolong kirim bukti transfernya ya kak."
                    <div style={{ fontSize: 14, color: "#6B7280", marginTop: 4 }}>17:40</div>
                  </div>
                )}
              </div>

              {/* Red GHOSTING Stamp Slam */}
              {frame >= 140 && (
                <div
                  style={{
                    position: "absolute",
                    inset: 0,
                    backgroundColor: "rgba(230, 57, 70, 0.92)",
                    backdropFilter: "blur(4px)",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    padding: 40,
                    transform: `scale(${spring({
                      frame: frame - 140,
                      fps,
                      config: { damping: 10, stiffness: 220 },
                    })})`,
                  }}
                >
                  <XCircle size={88} color="#FFFFFF" style={{ marginBottom: 20 }} />
                  <div
                    style={{
                      fontSize: 56,
                      fontWeight: 900,
                      color: "#FFFFFF",
                      letterSpacing: "0.08em",
                      textAlign: "center",
                      lineHeight: 1.15,
                    }}
                  >
                    GHOSTING! <br />
                    BATAL BELI.
                  </div>
                  <div
                    style={{
                      marginTop: 20,
                      fontSize: 22,
                      fontWeight: 700,
                      color: "#FEE2E2",
                      textAlign: "center",
                    }}
                  >
                    Alur manual bikin pembeli malas dan kabur.
                  </div>
                </div>
              )}
            </div>
          </div>
        </AbsoluteFill>
      )}

      {/* =========================================================================
          BEAT 2: THE RADICAL PIVOT TO NIXLEV (Frames 260 - 480 / 8.7s - 16.0s)
          ========================================================================= */}
      {frame >= 260 && frame < 480 && (
        <AbsoluteFill
          style={{
            backgroundColor: greenForest,
            color: "#FFFFFF",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "flex-start",
            padding: "90px 50px",
          }}
        >
          {/* Eyebrow */}
          <div
            style={{
              padding: "10px 28px",
              backgroundColor: "rgba(255,255,255,0.12)",
              borderRadius: 30,
              fontSize: 24,
              fontWeight: 800,
              letterSpacing: "0.08em",
              color: goldAccent,
              marginBottom: 35,
              textTransform: "uppercase",
              display: "flex",
              alignItems: "center",
              gap: 12,
            }}
          >
            <Sparkles size={24} />
            Cara Baru Jualan Digital
          </div>

          {/* Official HD Logo with Elastic Settle */}
          <div
            style={{
              transform: `scale(${spring({
                frame: frame - 260,
                fps,
                config: { damping: 14, stiffness: 140 },
              })}) rotate(${interpolate(frame, [260, 320], [-15, 0], {
                easing: Easing.bezier(0.16, 1, 0.3, 1),
                extrapolateRight: "clamp",
              })}deg)`,
              marginBottom: 30,
            }}
          >
            <Img
              src={staticFile("nixlev_logo.png")}
              style={{
                width: 220,
                height: 220,
                objectFit: "contain",
                filter: "drop-shadow(0 25px 50px rgba(0,0,0,0.4))",
              }}
            />
          </div>

          <h1
            style={{
              fontSize: 72,
              fontWeight: 900,
              letterSpacing: "-0.04em",
              marginBottom: 15,
              textAlign: "center",
            }}
          >
            Nixlev.com
          </h1>

          {/* Kinetic Typography Mask Reveal */}
          <div
            style={{
              overflow: "hidden",
              height: 55,
              marginBottom: 25,
            }}
          >
            <div
              style={{
                fontSize: 34,
                fontWeight: 700,
                color: "#E2E8F0",
                transform: `translateY(${interpolate(frame, [280, 310], [55, 0], {
                  easing: Easing.bezier(0.16, 1, 0.3, 1),
                  extrapolateRight: "clamp",
                })}px)`,
              }}
            >
              Bukan Sekadar Bio Link Pajangan.
            </div>
          </div>

          {/* Big Green Badge */}
          <div
            style={{
              padding: "18px 36px",
              backgroundColor: "rgba(16, 185, 129, 0.2)",
              border: "2px solid #10B981",
              borderRadius: 24,
              fontSize: 30,
              fontWeight: 900,
              color: "#34D399",
              letterSpacing: "0.02em",
              marginBottom: 40,
              boxShadow: "0 15px 40px rgba(16, 185, 129, 0.25)",
            }}
          >
            ⚡ MESIN PENJUALAN OTOMATIS
          </div>

          {/* 5 Minute Launch Counter */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 16,
              fontSize: 24,
              fontWeight: 600,
              color: "#CBD5E1",
            }}
          >
            <Clock size={28} color={goldAccent} />
            <span>Siap Jualan Dalam 5 Menit · Tanpa Koding</span>
          </div>
        </AbsoluteFill>
      )}

      {/* =========================================================================
          BEAT 3: 3D DESKTOP SHOWCASE & DECK-DEAL FLYING CARDS (Frames 480 - 780 / 16.0s - 26.0s)
          ========================================================================= */}
      {frame >= 480 && frame < 780 && (
        <AbsoluteFill
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "flex-start",
            padding: "60px 30px",
          }}
        >
          {/* Top Pill */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 12,
              padding: "12px 28px",
              backgroundColor: "#DCFCE7",
              color: "#15803D",
              borderRadius: 30,
              fontSize: 24,
              fontWeight: 800,
              marginBottom: 20,
            }}
          >
            <Layers size={26} />
            Multi-Produk Digital & Jasa
          </div>

          <h2
            style={{
              fontSize: 50,
              fontWeight: 900,
              textAlign: "center",
              lineHeight: 1.2,
              marginBottom: 30,
              letterSpacing: "-0.02em",
            }}
          >
            Upload Apa Saja Karya Digitalmu
          </h2>

          {/* 3D Isometric Desktop Window Behind */}
          <div
            style={{
              width: 960,
              height: 560,
              backgroundColor: "#FFFFFF",
              borderRadius: 28,
              border: "1px solid #E5E7EB",
              boxShadow: "0 30px 70px rgba(0,0,0,0.14)",
              overflow: "hidden",
              position: "relative",
              transform: `perspective(1200px) rotateX(12deg) rotateY(-6deg) scale(0.96) translateY(${bob2}px)`,
              marginBottom: -140,
              zIndex: 1,
            }}
          >
            {/* Safari Bar */}
            <div
              style={{
                height: 48,
                backgroundColor: "#F9FAFB",
                borderBottom: "1px solid #E5E7EB",
                display: "flex",
                alignItems: "center",
                padding: "0 20px",
                gap: 12,
              }}
            >
              <div style={{ display: "flex", gap: 8 }}>
                <div style={{ width: 12, height: 12, borderRadius: "50%", backgroundColor: "#FF5F56" }} />
                <div style={{ width: 12, height: 12, borderRadius: "50%", backgroundColor: "#FFBD2E" }} />
                <div style={{ width: 12, height: 12, borderRadius: "50%", backgroundColor: "#27C93F" }} />
              </div>
              <div
                style={{
                  flex: 1,
                  height: 30,
                  backgroundColor: "#FFFFFF",
                  border: "1px solid #E5E7EB",
                  borderRadius: 8,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 15,
                  fontWeight: 600,
                  color: "#374151",
                  gap: 8,
                }}
              >
                <Globe size={14} color="#10B981" />
                https://nixlev.com
              </div>
            </div>

            {/* Real Desktop Screenshot */}
            <Img
              src={staticFile("nixlev_desktop.png")}
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                objectPosition: "top center",
              }}
            />
          </div>

          {/* 4 Deck-Deal Flying Cards */}
          <div
            style={{
              width: "100%",
              maxWidth: 980,
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 20,
              zIndex: 10,
              padding: "0 20px",
            }}
          >
            {[
              {
                title: "E-Book & PDF Panduan",
                price: "Rp 99.000",
                icon: BookOpen,
                delay: 500,
                color: "#2563EB",
                bg: "#EFF6FF",
                rotate: -4,
              },
              {
                title: "Template Notion & Canva",
                price: "Rp 149.000",
                icon: FileText,
                delay: 550,
                color: "#7C3AED",
                bg: "#F5F3FF",
                rotate: 5,
              },
              {
                title: "Kelas Video / Course",
                price: "Rp 299.000",
                icon: GraduationCap,
                delay: 600,
                color: "#D97706",
                bg: "#FFFBEB",
                rotate: -3,
              },
              {
                title: "1-on-1 Jasa Konsultasi",
                price: "Rp 499.000",
                icon: Briefcase,
                delay: 650,
                color: "#059669",
                bg: "#ECFDF5",
                rotate: 4,
              },
            ].map((card, i) => {
              const IconComponent = card.icon;
              const cardSpring = spring({
                frame: frame - card.delay,
                fps,
                config: { damping: 13, stiffness: 150 },
              });

              return (
                <div
                  key={i}
                  style={{
                    backgroundColor: "#FFFFFF",
                    borderRadius: 22,
                    padding: "20px 24px",
                    border: "2px solid #E5E7EB",
                    boxShadow: "0 20px 45px rgba(0,0,0,0.14)",
                    display: "flex",
                    alignItems: "center",
                    gap: 18,
                    transform: `scale(${cardSpring}) rotate(${card.rotate}deg) translateY(${
                      (1 - cardSpring) * 100
                    }px)`,
                    opacity: cardSpring,
                  }}
                >
                  <div
                    style={{
                      width: 58,
                      height: 58,
                      borderRadius: 16,
                      backgroundColor: card.bg,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <IconComponent size={32} color={card.color} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 22, fontWeight: 800 }}>{card.title}</div>
                    <div style={{ fontSize: 19, fontWeight: 700, color: card.color, marginTop: 4 }}>
                      {card.price}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </AbsoluteFill>
      )}

      {/* =========================================================================
          BEAT 4: CRASH ZOOM INTO QRIS & DUAL DELIVERY (Frames 780 - 1200 / 26.0s - 40.0s)
          ========================================================================= */}
      {frame >= 780 && frame < 1200 && (
        <AbsoluteFill
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "flex-start",
            padding: "70px 40px",
          }}
        >
          {/* Top Pill */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 12,
              padding: "12px 28px",
              backgroundColor: "#DCFCE7",
              color: "#15803D",
              borderRadius: 30,
              fontSize: 24,
              fontWeight: 800,
              marginBottom: 20,
            }}
          >
            <Zap size={26} color={goldAccent} />
            Scan QRIS ➔ Langsung Terkirim
          </div>

          <h2
            style={{
              fontSize: 50,
              fontWeight: 900,
              textAlign: "center",
              lineHeight: 1.15,
              marginBottom: 35,
              letterSpacing: "-0.02em",
            }}
          >
            Scan Langsung di Layar <br />
            <span style={{ color: greenForest }}>Dual Delivery Instan ⚡</span>
          </h2>

          {/* Glossy Black VIP QRIS Card */}
          <div
            style={{
              width: 420,
              backgroundColor: "#111827",
              borderRadius: 34,
              padding: "32px 26px",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              boxShadow: "0 35px 80px rgba(0,0,0,0.3)",
              border: "1px solid rgba(255,255,255,0.15)",
              position: "relative",
              marginBottom: 30,
              transform: `scale(${spring({
                frame: frame - 780,
                fps,
                config: { damping: 14, stiffness: 150 },
              })})`,
            }}
          >
            <div
              style={{
                fontSize: 19,
                fontWeight: 800,
                color: "#9CA3AF",
                letterSpacing: "0.1em",
                marginBottom: 16,
                textTransform: "uppercase",
              }}
            >
              QRIS Dinamis Otomatis
            </div>

            {/* Glowing QR Box */}
            <div
              style={{
                width: 230,
                height: 230,
                backgroundColor: "#FFFFFF",
                borderRadius: 22,
                padding: 14,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                position: "relative",
                overflow: "hidden",
              }}
            >
              <QrCode size={195} color="#111827" />

              {/* Laser Scan Sweep Line */}
              <div
                style={{
                  position: "absolute",
                  left: 0,
                  right: 0,
                  height: 6,
                  backgroundColor: greenEmerald,
                  boxShadow: "0 0 20px #10B981, 0 0 40px #10B981",
                  top: `${interpolate(frame, [800, 940], [0, 224], {
                    easing: Easing.bezier(0.16, 1, 0.3, 1),
                  })}%`,
                }}
              />
            </div>

            <div
              style={{
                marginTop: 18,
                fontSize: 22,
                fontWeight: 800,
                color: "#10B981",
                display: "flex",
                alignItems: "center",
                gap: 10,
              }}
            >
              <CheckCircle2 size={24} />
              Pembayaran Berhasil
            </div>
          </div>

          {/* DUAL ROCKET NOTIFICATIONS (WhatsApp + Email) POPPING IN */}
          <div style={{ width: "100%", maxWidth: 860, display: "flex", flexDirection: "column", gap: 18 }}>
            {/* WhatsApp Notification Card */}
            {frame >= 960 && (
              <div
                style={{
                  backgroundColor: "#FFFFFF",
                  borderRadius: 22,
                  padding: "22px 28px",
                  border: "2px solid #22C55E",
                  boxShadow: "0 20px 45px rgba(34, 197, 94, 0.18)",
                  display: "flex",
                  alignItems: "center",
                  gap: 20,
                  transform: `translateX(${interpolate(frame, [960, 990], [-60, 0], {
                    easing: Easing.bezier(0.16, 1, 0.3, 1),
                    extrapolateRight: "clamp",
                  })}px)`,
                }}
              >
                <div
                  style={{
                    width: 62,
                    height: 62,
                    borderRadius: 18,
                    backgroundColor: "#DCFCE7",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <MessageSquare size={34} color="#15803D" />
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 24, fontWeight: 800, color: "#14532D" }}>
                    Terkirim Instan ke WhatsApp Pembeli
                  </div>
                  <div style={{ fontSize: 18, color: "#4B5563", marginTop: 4 }}>
                    "Hai! Pesanan #NX-882 selesai. Klik link untuk download e-book kamu ➜"
                  </div>
                </div>
                <div
                  style={{
                    backgroundColor: "#DCFCE7",
                    color: "#15803D",
                    padding: "8px 16px",
                    borderRadius: 12,
                    fontSize: 16,
                    fontWeight: 800,
                  }}
                >
                  24 JAM
                </div>
              </div>
            )}

            {/* Email Notification Card */}
            {frame >= 1040 && (
              <div
                style={{
                  backgroundColor: "#FFFFFF",
                  borderRadius: 22,
                  padding: "22px 28px",
                  border: "2px solid #3B82F6",
                  boxShadow: "0 20px 45px rgba(59, 130, 246, 0.18)",
                  display: "flex",
                  alignItems: "center",
                  gap: 20,
                  transform: `translateX(${interpolate(frame, [1040, 1070], [60, 0], {
                    easing: Easing.bezier(0.16, 1, 0.3, 1),
                    extrapolateRight: "clamp",
                  })}px)`,
                }}
              >
                <div
                  style={{
                    width: 62,
                    height: 62,
                    borderRadius: 18,
                    backgroundColor: "#EFF6FF",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Mail size={34} color="#2563EB" />
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 24, fontWeight: 800, color: "#1E3A8A" }}>
                    Terkirim Otomatis ke Email
                  </div>
                  <div style={{ fontSize: 18, color: "#4B5563", marginTop: 4 }}>
                    Invoice resmi pembayaran + akses file cadangan berlisensi.
                  </div>
                </div>
                <div
                  style={{
                    backgroundColor: "#DBEAFE",
                    color: "#1D4ED8",
                    padding: "8px 16px",
                    borderRadius: 12,
                    fontSize: 16,
                    fontWeight: 800,
                  }}
                >
                  EMAIL
                </div>
              </div>
            )}
          </div>
        </AbsoluteFill>
      )}

      {/* =========================================================================
          BEAT 5: THE FREEDOM / ZERO HOSTING DRAMA (Frames 1200 - 1460 / 40.0s - 48.7s)
          ========================================================================= */}
      {frame >= 1200 && frame < 1460 && (
        <AbsoluteFill
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "flex-start",
            padding: "90px 40px",
          }}
        >
          <div
            style={{
              padding: "10px 28px",
              backgroundColor: "#FEF3C7",
              color: "#D97706",
              borderRadius: 30,
              fontSize: 24,
              fontWeight: 800,
              marginBottom: 30,
              textTransform: "uppercase",
            }}
          >
            Bebas Beban Operasional
          </div>

          <h2
            style={{
              fontSize: 54,
              fontWeight: 900,
              textAlign: "center",
              lineHeight: 1.2,
              marginBottom: 45,
            }}
          >
            Fokus Berkarya. <br />
            <span style={{ color: greenForest }}>Nixlev yang Urus Semuanya.</span>
          </h2>

          <div style={{ width: "100%", maxWidth: 840, display: "flex", flexDirection: "column", gap: 24 }}>
            {/* Abolished Drama 1 */}
            <div
              style={{
                backgroundColor: "#FFFFFF",
                borderRadius: 24,
                padding: "26px 32px",
                border: "2px solid #FEE2E2",
                boxShadow: "0 15px 35px rgba(0,0,0,0.06)",
                display: "flex",
                alignItems: "center",
                gap: 22,
              }}
            >
              <div
                style={{
                  width: 64,
                  height: 64,
                  borderRadius: 18,
                  backgroundColor: "#FEE2E2",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <XCircle size={36} color="#DC2626" />
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 26, fontWeight: 800, color: textDark }}>
                  Gak Perlu Begadang Cek Mutasi Bank
                </div>
                <div style={{ fontSize: 20, color: textMuted }}>
                  Semua transaksi QRIS diverifikasi instan secara otomatis oleh sistem.
                </div>
              </div>
            </div>

            {/* Abolished Drama 2 */}
            <div
              style={{
                backgroundColor: "#FFFFFF",
                borderRadius: 24,
                padding: "26px 32px",
                border: "2px solid #FEE2E2",
                boxShadow: "0 15px 35px rgba(0,0,0,0.06)",
                display: "flex",
                alignItems: "center",
                gap: 22,
              }}
            >
              <div
                style={{
                  width: 64,
                  height: 64,
                  borderRadius: 18,
                  backgroundColor: "#FEE2E2",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <XCircle size={36} color="#DC2626" />
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 26, fontWeight: 800, color: textDark }}>
                  Gak Perlu Sewa Hosting Jutaan Rupiah
                </div>
                <div style={{ fontSize: 20, color: textMuted }}>
                  Nixlev berjalan di cloud berkecepatan tinggi, tanpa tagihan server.
                </div>
              </div>
            </div>
          </div>
        </AbsoluteFill>
      )}

      {/* =========================================================================
          BEAT 6: OUTRO KINETIC PUNCH & CTA (Frames 1460 - 1700 / 48.7s - 56.7s)
          *Permintaan Khusus Zura: Frame 1460 Logo Dibuat Hijau Tema Nixlev Biar Terlihat Jelas & Standout*
          ========================================================================= */}
      {frame >= 1460 && (
        <AbsoluteFill
          style={{
            backgroundColor: greenForest,
            color: "#FFFFFF",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "flex-start",
            padding: "80px 50px",
          }}
        >
          {/* Logo Hero Card with Signature Nixlev Emerald Neon Glow & Theme Green Container */}
          <div
            style={{
              padding: 24,
              borderRadius: "50%",
              backgroundColor: "rgba(255, 255, 255, 0.12)",
              border: `3px solid ${goldAccent}`,
              boxShadow: "0 0 50px rgba(16, 185, 129, 0.45), 0 20px 45px rgba(0,0,0,0.4)",
              transform: `scale(${spring({
                frame: frame - 1460,
                fps,
                config: { damping: 14, stiffness: 120 },
              })})`,
              marginBottom: 25,
            }}
          >
            <Img
              src={staticFile("nixlev_logo.png")}
              style={{
                width: 170,
                height: 170,
                objectFit: "contain",
                filter: "drop-shadow(0 15px 30px rgba(0,0,0,0.5))",
              }}
            />
          </div>

          <h1
            style={{
              fontSize: 62,
              fontWeight: 900,
              textAlign: "center",
              lineHeight: 1.15,
              marginBottom: 30,
              letterSpacing: "-0.03em",
            }}
          >
            Saatnya Bio Link Kamu <br />
            <span style={{ color: "#34D399" }}>Menghasilkan Uang Otomatis.</span>
          </h1>

          {/* 3 Value Checkpoints */}
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 14,
              width: "100%",
              maxWidth: 760,
              marginBottom: 35,
            }}
          >
            {[
              "0 Biaya Hosting & 0 Koding",
              "QRIS Instan Langsung Terhubung",
              "Dual Delivery Otomatis ke WhatsApp & Email",
            ].map((text, idx) => (
              <div
                key={idx}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 16,
                  fontSize: 22,
                  fontWeight: 700,
                  color: "#E2E8F0",
                }}
              >
                <div
                  style={{
                    width: 34,
                    height: 34,
                    borderRadius: "50%",
                    backgroundColor: "rgba(16, 185, 129, 0.25)",
                    border: "1px solid #10B981",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <CheckCircle2 size={22} color="#34D399" />
                </div>
                {text}
              </div>
            ))}
          </div>

          {/* Forest Green CTA Pill with Sweeping Shine */}
          <div
            style={{
              backgroundColor: "#10B981",
              color: "#FFFFFF",
              padding: "24px 54px",
              borderRadius: 32,
              fontSize: 32,
              fontWeight: 900,
              display: "flex",
              alignItems: "center",
              gap: 18,
              boxShadow: "0 20px 50px rgba(16, 185, 129, 0.4)",
              transform: `scale(${1 + Math.sin(frame / 12) * 0.025})`,
              position: "relative",
              overflow: "hidden",
            }}
          >
            <span>Mulai Buat Gratis di nixlev.com</span>
            <ArrowRight size={36} color="#FFFFFF" />

            {/* Sweeping Shine Overlay */}
            <div
              style={{
                position: "absolute",
                top: 0,
                bottom: 0,
                width: 90,
                background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.5), transparent)",
                transform: `translateX(${interpolate(frame % 90, [0, 90], [-100, 600])}px) skewX(-20deg)`,
              }}
            />
          </div>

          <div
            style={{
              marginTop: 25,
              fontSize: 22,
              fontWeight: 600,
              color: "#94A3B8",
            }}
          >
            Daftar gratis sekarang ➔ nixlev.com
          </div>
        </AbsoluteFill>
      )}

      {/* =========================================================================
          DYNAMIC SUBTITLES COMPONENT (SAFE ZONE: bottom 220px)
          Designed to stay completely clear of TikTok / Instagram Reels UI
          ========================================================================= */}
      {currentSub && (
        <div
          style={{
            position: "absolute",
            bottom: 220, // Strict Safe Zone for TikTok/Reels UI
            left: 0,
            right: 0,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            padding: "0 40px",
            zIndex: 100,
            pointerEvents: "none",
          }}
        >
          <div
            style={{
              backgroundColor: "rgba(18, 24, 20, 0.90)",
              backdropFilter: "blur(12px)",
              WebkitBackdropFilter: "blur(12px)",
              padding: "16px 36px",
              borderRadius: 24,
              border: "1px solid rgba(255, 255, 255, 0.14)",
              boxShadow: "0 15px 35px rgba(0,0,0,0.35)",
              maxWidth: 920,
              textAlign: "center",
              transform: `scale(${spring({
                frame: frame - currentSub.from,
                fps,
                config: { damping: 14, stiffness: 180 },
              })})`,
            }}
          >
            <span
              style={{
                fontSize: 30,
                fontWeight: 800,
                color: "#FFFFFF",
                letterSpacing: "-0.01em",
                lineHeight: 1.3,
              }}
            >
              {currentSub.highlight ? (
                <>
                  {currentSub.text.split(currentSub.highlight)[0]}
                  <span
                    style={{
                      color: currentSub.isRed
                        ? "#F87171"
                        : frame >= 1460
                        ? "#34D399"
                        : goldAccent,
                      textDecoration: currentSub.isRed ? "underline" : "none",
                    }}
                  >
                    {currentSub.highlight}
                  </span>
                  {currentSub.text.split(currentSub.highlight)[1]}
                </>
              ) : (
                currentSub.text
              )}
            </span>
          </div>
        </div>
      )}
    </AbsoluteFill>
  );
};
